import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type RxCollection } from 'rxdb'
import { toast } from 'ui/sonner'

import { replicateCards } from '~/db/replication'
import type { Card } from '~/db/types'
import { useIsOnline } from '~/hooks/use-is-online'
import { useAccountStore } from '~/stores/account'

export const useCardsReplication = (cardsCollection: RxCollection<Card> | null) => {
  const [isSyncing, setIsSyncing] = useState(false)
  const { t } = useTranslation()
  const isOnline = useIsOnline()
  const planId = useAccountStore(state => state.planId)
  const replicationRef = useRef<ReturnType<typeof replicateCards> | undefined>(undefined)
  const startReplicationRunRef = useRef<() => void>(() => {})
  const wasOfflineRef = useRef(!isOnline)

  useEffect(() => {
    if (!cardsCollection || !planId || !isOnline) return

    const startReplicationRun = () => {
      void replicationRef.current?.cancel?.()

      replicationRef.current = replicateCards(cardsCollection, planId, {
        onStart: () => {
          setIsSyncing(true)
        },
        onError: () => {
          toast.error(t('Sync error'), { position: 'top-right' })
          setIsSyncing(false)
        },
        onFinish: () => {
          setIsSyncing(false)
        },
      })

      void replicationRef.current?.start()
    }

    startReplicationRunRef.current = startReplicationRun
    startReplicationRun()

    const resyncOnHideDocument = () => {
      if (document.visibilityState !== 'hidden') return

      startReplicationRun()
    }
    document.addEventListener('visibilitychange', resyncOnHideDocument)

    return () => {
      document.removeEventListener('visibilitychange', resyncOnHideDocument)
      void replicationRef.current?.cancel?.()
      replicationRef.current = undefined
      startReplicationRunRef.current = () => {}
    }
  }, [cardsCollection, planId, t, isOnline])

  useEffect(() => {
    if (!cardsCollection || !planId) return

    if (!isOnline) {
      wasOfflineRef.current = true
      return
    }

    if (!wasOfflineRef.current) return

    wasOfflineRef.current = false
    startReplicationRunRef.current()
  }, [cardsCollection, isOnline, planId])

  return isSyncing
}
